import { useState, useEffect } from "react";
import { Shield, ScrollText, Lock, CheckCircle, CheckCircle2, Eye, X, Mail, Phone, Palette, FileDown, Loader2, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../config";
import { useAuth } from "../context/AuthContext";

// UI Style Reference — Flusso Cliente (req. Fabrizio 07/07/2026).
// Pagina interna statica da condividere con Zorzutti: documenta lo
// standard grafico condiviso di Step 1/2/3 (colori, banner, barre,
// bottoni) così le review partono da un riferimento comune.

const SWATCHES = [
  { hex: "#E31837", name: "LoJack Red", usage: "Brand, CTA primarie, icone step, toggle attivi" },
  { hex: "#2B648F", name: "LoJack Blue", usage: "Stati confermati/positivi (lettura completata, firma OK)" },
  { hex: "#FACC15", name: "Giallo Warning", usage: "Azione richiesta dal cliente (banner scroll/countdown, barre in attesa)" },
  { hex: "#6E0C0C", name: "Bordeaux Clause", usage: "Clausole vessatorie, evidenziazioni legali" },
  { hex: "#0A0A0A", name: "Nero LoJack", usage: "Sfondo pagina e riquadri interni" },
  { hex: "#1A1A1A", name: "Dark", usage: "Card, navbar, superfici elevate" },
  { hex: "#374151", name: "Gray-700", usage: "Bordi riquadri e separatori" },
];

const GOLDEN_RULES = [
  "Stesso schema grafico su TUTTE le schermate: se un elemento cambia in uno step, va allineato anche negli altri (req. Zorzutti 07/07/2026).",
  "Giallo = azione richiesta al cliente (scroll, attesa countdown). Blu = stato confermato. Rosso = brand e CTA primarie.",
  "Barra di avanzamento lettura: SEMPRE full-width, alta 4px (h-1), posta SOTTO la stringa di istruzione — mai laterale.",
  "Banner di stato lettura: su mobile in cima al riquadro PDF, su desktop sotto il PDF.",
  "Checkbox obbligatorie disabilitate (opacity-50) finché la condizione non è soddisfatta.",
  "Un solo titolo per step: niente titoli duplicati tra header e contenuto (razionalizzazione testi 07/07/2026).",
];

const SectionTitle = ({ children }) => (
  <h2 className="text-lg font-semibold text-white mt-10 mb-1 flex items-center gap-2">
    <span className="w-1.5 h-5 bg-lojack-red rounded-full inline-block" />
    {children}
  </h2>
);

const Note = ({ children }) => (
  <p className="text-gray-500 text-xs mb-4">{children}</p>
);

// Demo barra animata 0→100% in loop, per mostrare il comportamento live.
const useDemoProgress = () => {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setPct((p) => (p >= 100 ? 0 : p + 10)), 700);
    return () => clearInterval(id);
  }, []);
  return pct;
};

// Modal invio email con destinatari multipli (chips) e messaggio opzionale.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SendEmailModal = ({ token, onClose }) => {
  const [recipients, setRecipients] = useState([]);
  const [input, setInput] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const addFromInput = (raw) => {
    const parts = (raw || "").split(/[,;\s]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);
    if (!parts.length) return;
    const valid = parts.filter((p) => EMAIL_RE.test(p));
    const invalid = parts.filter((p) => !EMAIL_RE.test(p));
    if (invalid.length) toast.error(`Email non valida: ${invalid.join(", ")}`);
    if (valid.length) setRecipients((prev) => [...new Set([...prev, ...valid])].slice(0, 10));
    setInput("");
  };

  const handleKeyDown = (e) => {
    if (["Enter", ",", ";", " "].includes(e.key)) {
      e.preventDefault();
      addFromInput(input);
    } else if (e.key === "Backspace" && !input && recipients.length) {
      setRecipients((prev) => prev.slice(0, -1));
    }
  };

  const handleSend = async () => {
    const all = [...recipients];
    if (input.trim() && EMAIL_RE.test(input.trim().toLowerCase())) all.push(input.trim().toLowerCase());
    if (!all.length) { toast.error("Aggiungi almeno un destinatario"); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/style-reference/send-email`,
        { recipients: [...new Set(all)], message: message.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`PDF inviato a ${res.data.count} destinatari${res.data.count === 1 ? "o" : ""}`);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invio email fallito");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4" data-testid="style-ref-email-modal">
      <div className="bg-[#111] border border-gray-700 rounded-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-white font-semibold flex items-center gap-2"><Send size={16} className="text-lojack-red" /> Invia Style Reference via email</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white" data-testid="style-ref-email-close"><X size={18} /></button>
        </div>
        <p className="text-gray-500 text-xs mb-4">Il PDF verrà generato e allegato automaticamente. Max 10 destinatari.</p>

        <label className="text-gray-400 text-xs font-medium">Destinatari</label>
        <div className="mt-1 mb-3 flex flex-wrap items-center gap-1.5 px-3 py-2 bg-[#0A0A0A] border border-gray-700 rounded-xl focus-within:border-lojack-red" data-testid="style-ref-email-recipients">
          {recipients.map((r) => (
            <span key={r} className="inline-flex items-center gap-1 px-2 py-0.5 bg-lojack-red/15 border border-lojack-red/40 text-white text-xs rounded-full">
              {r}
              <button onClick={() => setRecipients((prev) => prev.filter((x) => x !== r))} className="text-gray-400 hover:text-white"><X size={11} /></button>
            </span>
          ))}
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => addFromInput(input)}
            placeholder={recipients.length ? "" : "es. azorzutti@lojack.it, collega@lojack.it"}
            className="flex-1 min-w-[140px] bg-transparent text-white text-sm focus:outline-none placeholder:text-gray-600"
            data-testid="style-ref-email-input"
          />
        </div>

        <label className="text-gray-400 text-xs font-medium">Messaggio (opzionale)</label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={3}
          placeholder="es. Buongiorno, in allegato lo standard grafico aggiornato come concordato…"
          className="mt-1 w-full px-3 py-2 bg-[#0A0A0A] border border-gray-700 rounded-xl text-white text-sm focus:border-lojack-red focus:outline-none placeholder:text-gray-600 resize-none"
          data-testid="style-ref-email-message"
        />

        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 py-2.5 border border-gray-600 rounded-xl text-gray-400 hover:bg-gray-800 transition-colors text-sm" data-testid="style-ref-email-cancel">
            Annulla
          </button>
          <button
            onClick={handleSend}
            disabled={sending}
            className="flex-1 py-2.5 bg-lojack-red hover:bg-[#B81430] disabled:opacity-50 text-white font-semibold rounded-xl transition-colors text-sm inline-flex items-center justify-center gap-2"
            data-testid="style-ref-email-submit"
          >
            {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            {sending ? "Invio..." : "Invia"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function AdminStyleReferencePage() {
  const pct = useDemoProgress();
  const { token } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/style-reference/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "LoJack_UI_Style_Reference.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF esportato — pronto da allegare");
    } catch {
      toast.error("Errore durante l'esportazione del PDF");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-lojack-black py-8 px-4" data-testid="style-reference-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Palette size={24} className="text-lojack-red" />
              <h1 className="text-2xl font-bold text-white">UI Style Reference — Flusso Cliente</h1>
            </div>
            <p className="text-gray-400 text-sm">
              Standard grafico condiviso degli step cliente (Dati → GDPR → Firma). Riferimento per le review di coerenza visiva.
            </p>
            <p className="text-gray-600 text-xs mt-1">Rev. 07/07/2026 — armonizzazione barre di scorrimento Step 2/3 e razionalizzazione testi Step 2 (mockup A. Zorzutti)</p>
          </div>
          <div className="shrink-0 flex items-center gap-2">
            <button
              onClick={() => setShowEmailModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-gray-600 hover:bg-gray-800 text-gray-300 text-sm font-semibold rounded-xl transition-colors"
              data-testid="style-ref-send-email-btn"
            >
              <Send size={16} /> Invia via email
            </button>
            <button
              onClick={handleExportPdf}
              disabled={exporting}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-lojack-red hover:bg-[#B81430] disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors"
              data-testid="style-ref-export-pdf-btn"
            >
              {exporting ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />}
              {exporting ? "Esportazione..." : "Esporta PDF"}
            </button>
          </div>
        </div>
        {showEmailModal && <SendEmailModal token={token} onClose={() => setShowEmailModal(false)} />}

        {/* ── Palette ── */}
        <SectionTitle>Palette colori</SectionTitle>
        <Note>Colori istituzionali e semantici usati nel flusso cliente.</Note>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="style-ref-palette">
          {SWATCHES.map((s) => (
            <div key={s.hex} className="flex items-center gap-3 rounded-xl border border-gray-700 bg-[#111] p-3">
              <div className="w-12 h-12 rounded-lg border border-gray-600 shrink-0" style={{ backgroundColor: s.hex }} />
              <div className="min-w-0">
                <p className="text-white text-sm font-semibold">{s.name} <span className="font-mono text-gray-500 text-xs ml-1">{s.hex}</span></p>
                <p className="text-gray-400 text-xs">{s.usage}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Tipografia step ── */}
        <SectionTitle>Tipografia degli step</SectionTitle>
        <Note>Gerarchia unica per ogni step: etichetta step → titolo con icona → sottotitolo → label di sezione. Nessun titolo duplicato.</Note>
        <div className="rounded-xl border border-gray-700 bg-[#111] p-5 space-y-2" data-testid="style-ref-typography">
          <span className="text-lojack-red text-xs uppercase tracking-wider font-semibold">Step 2 di 3</span>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Shield size={20} className="text-lojack-red" />
            GDPR UE 2016/679 art. 13
          </h2>
          <p className="text-gray-400 text-sm">Leggi attentamente l'informativa privacy, accettala ed esprimi le eventuali preferenze di contatto</p>
          <p className="text-white font-semibold text-sm pt-2">Informativa privacy</p>
        </div>

        {/* ── Banner di stato lettura ── */}
        <SectionTitle>Banner di stato lettura (standard armonizzato)</SectionTitle>
        <Note>
          Barra di avanzamento SEMPRE full-width sotto la stringa (schema unico Step 2 e Step 3).
          Posizione del banner: mobile → in cima al riquadro PDF · desktop → sotto il PDF.
        </Note>
        <div className="space-y-3" data-testid="style-ref-banners">
          {/* Variante scroll (desktop) */}
          <div className="px-3 py-2 bg-yellow-500/10 border border-yellow-500/40 rounded-lg">
            <div className="flex items-center gap-2">
              <ScrollText size={14} className="text-yellow-400 shrink-0" />
              <p className="text-yellow-300 font-medium text-xs flex-1">Scorri l'informativa fino in fondo per poter confermare la lettura</p>
            </div>
            <div className="mt-2 h-1 bg-gray-800 rounded-full overflow-hidden">
              <div className="h-full bg-yellow-400 transition-all duration-700" style={{ width: `${pct}%` }} />
            </div>
          </div>
          <p className="text-gray-600 text-xs pl-1">↑ Variante <b>scroll</b> (desktop) — icona ScrollText, barra pilotata dallo scroll del PDF</p>

          {/* Variante countdown (mobile) */}
          <div className="px-3 py-2 bg-yellow-500/10 border border-yellow-500/40 rounded-lg">
            <div className="flex items-center gap-2">
              <Lock size={14} className="text-yellow-400 shrink-0" />
              <p className="text-yellow-300 font-medium text-xs flex-1">
                Leggi l'informativa privacy per qualche secondo
                <span className="ml-1 font-mono text-yellow-200">({Math.max(0, 15 - Math.round(pct * 0.15))}s)</span>
              </p>
            </div>
            <div className="mt-2 h-1 bg-gray-800 rounded-full overflow-hidden">
              <div className="h-full bg-yellow-400 transition-all duration-700" style={{ width: `${pct}%` }} />
            </div>
          </div>
          <p className="text-gray-600 text-xs pl-1">↑ Variante <b>countdown</b> (mobile) — icona Lock, timer 15s in font mono</p>

          {/* Stato confermato */}
          <div className="flex items-center gap-2 px-3 py-2 bg-lojack-blue-500/10 border border-lojack-blue-500/30 rounded-lg">
            <CheckCircle size={14} className="text-lojack-blue-400 shrink-0" />
            <p className="text-lojack-blue-300 text-xs">Informativa visionata — ora puoi accettare</p>
          </div>
          <p className="text-gray-600 text-xs pl-1">↑ Stato <b>confermato</b> — blu istituzionale, sostituisce il banner giallo a lettura avvenuta</p>
        </div>

        {/* ── Barre di avanzamento ── */}
        <SectionTitle>Barra di avanzamento — specifica</SectionTitle>
        <div className="rounded-xl border border-gray-700 bg-[#111] p-5 space-y-4" data-testid="style-ref-progressbar">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-xs">
            <p className="text-gray-400"><span className="text-white font-semibold">Altezza:</span> 4px (<code className="text-yellow-300">h-1</code>)</p>
            <p className="text-gray-400"><span className="text-white font-semibold">Larghezza:</span> 100% del banner/pannello</p>
            <p className="text-gray-400"><span className="text-white font-semibold">Posizione:</span> sotto la stringa di istruzione (<code className="text-yellow-300">mt-2</code>)</p>
            <p className="text-gray-400"><span className="text-white font-semibold">Track:</span> <code className="text-yellow-300">bg-gray-800</code>, angoli pieni</p>
            <p className="text-gray-400"><span className="text-white font-semibold">Fill in attesa:</span> <code className="text-yellow-300">bg-yellow-400</code></p>
            <p className="text-gray-400"><span className="text-white font-semibold">Fill completato (Step 3):</span> <code className="text-yellow-300">bg-lojack-blue-500</code></p>
          </div>
          <div>
            <p className="text-gray-500 text-xs mb-1">In attesa ({pct}%)</p>
            <div className="h-1 bg-gray-800 rounded-full overflow-hidden">
              <div className="h-full bg-yellow-400 transition-all duration-700" style={{ width: `${pct}%` }} />
            </div>
          </div>
          <div>
            <p className="text-gray-500 text-xs mb-1">Completata (100%)</p>
            <div className="h-1 bg-gray-800 rounded-full overflow-hidden">
              <div className="h-full bg-lojack-blue-500" style={{ width: "100%" }} />
            </div>
          </div>
        </div>

        {/* ── Bottoni e controlli ── */}
        <SectionTitle>Bottoni e controlli</SectionTitle>
        <div className="rounded-xl border border-gray-700 bg-[#111] p-5 space-y-5" data-testid="style-ref-buttons">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-gray-500 text-xs mb-2">CTA primaria (<code className="text-yellow-300">btn-lojack</code>)</p>
              <button className="w-full btn-lojack flex items-center justify-center gap-2">
                <CheckCircle2 size={16} /> Conferma Preferenze
              </button>
            </div>
            <div>
              <p className="text-gray-500 text-xs mb-2">Secondario / Indietro</p>
              <button className="w-full py-3 border border-gray-600 rounded-xl text-gray-400 hover:bg-gray-800 transition-colors">Indietro</button>
            </div>
          </div>

          <div>
            <p className="text-gray-500 text-xs mb-2">Toggle consensi (tap-to-toggle, senza checkbox)</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="text-left p-4 rounded-xl border-2 border-lojack-red bg-lojack-red/10">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5 bg-lojack-red">
                    <CheckCircle size={14} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm text-white">Sondaggi e questionari</p>
                    <p className="text-gray-500 text-xs mt-0.5">Ricerche di mercato e questionari sui prodotti</p>
                    <p className="text-[10px] uppercase tracking-wider font-semibold mt-2 text-lojack-red">Accettato</p>
                  </div>
                </div>
              </div>
              <div className="text-left p-4 rounded-xl border-2 border-gray-700 bg-[#0A0A0A]">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5 bg-gray-700">
                    <X size={14} className="text-gray-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm text-gray-300">Azioni di marketing</p>
                    <p className="text-gray-500 text-xs mt-0.5">Newsletter, offerte promozionali e novità LoJack</p>
                    <p className="text-[10px] uppercase tracking-wider font-semibold mt-2 text-gray-500">Rifiutato</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <p className="text-gray-500 text-xs mb-2">Tile canale di contatto</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center justify-between p-4 rounded-xl border-2 border-lojack-red bg-lojack-red/10">
                <div className="flex items-center gap-2">
                  <Mail size={16} className="text-lojack-red" />
                  <div>
                    <p className="text-white text-sm font-semibold uppercase">E-mail</p>
                    <p className="text-gray-400 text-xs">cliente@esempio.it</p>
                  </div>
                </div>
                <CheckCircle size={16} className="text-lojack-red" />
              </div>
              <div className="flex items-center justify-between p-4 rounded-xl border-2 border-gray-700 bg-[#0A0A0A]">
                <div className="flex items-center gap-2">
                  <Phone size={16} className="text-gray-400" />
                  <div>
                    <p className="text-gray-300 text-sm font-semibold uppercase">Telefono</p>
                    <p className="text-gray-500 text-xs">+39 3401234567</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <p className="text-gray-500 text-xs mb-2">Link secondario (apertura documento)</p>
            <span className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 cursor-pointer">
              <Eye size={12} /> Visualizza l'Informativa per la privacy
            </span>
          </div>

          <div>
            <p className="text-gray-500 text-xs mb-2">Checkbox obbligatoria (bloccata finché il PDF non è stato letto)</p>
            <label className="flex items-start gap-3 cursor-not-allowed opacity-50">
              <input type="checkbox" disabled className="checkbox-lojack mt-1" />
              <div>
                <span className="text-white text-sm font-medium">Privacy*</span>
                <p className="text-gray-400 text-xs mt-1">"Autorizzo il trattamento dei dati personali sopra riportati ai sensi del Regolamento UE 2016/679 GDPR"</p>
              </div>
            </label>
          </div>
        </div>

        {/* ── Regole d'oro ── */}
        <SectionTitle>Regole d'oro di coerenza</SectionTitle>
        <ul className="space-y-2 mb-12" data-testid="style-ref-rules">
          {GOLDEN_RULES.map((r, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
              <span className="text-lojack-red font-bold shrink-0 mt-0.5">{i + 1}.</span>
              <span>{r}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
